import { useEffect, useMemo } from 'react';

import { type PropertyFieldTypesType } from 'customer-data-objects/property/PropertyFieldTypes';

import { PropertyGroupDescriptor } from 'framework-listing-lib/types';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useSkipCrm from 'framework-listing-lib/internal/hooks/useSkipCrm';
import { SensitiveDataCategoryType } from 'customer-data-objects/property/PropertySensitiveDataCategory';
import { PropertyTypesType } from 'customer-data-objects/property/PropertyTypes';
import { PropertyGroups } from 'hs-resources/resources/PropertyGroups';
import { ObjectTypeDefinitions } from 'hs-resources/resources/ObjectTypeDefinition';
import { useResource } from 'pulse/react';

export default function useFetchListingLibData({
  showHighlySensitiveProperties,
}: {
  showHighlySensitiveProperties?: boolean;
}) {
  'use memo';
  const objectTypeId = useObjectTypeId();
  const skipCrm = useSkipCrm();

  const {
    data: propertyGroupsData,
    loading: propertyGroupsloading,
    error: propertyGroupsError,
  } = useResource(PropertyGroups, {
    params: {
      objectTypeId,
      showHighlySensitiveProperties,
    },
    skip: skipCrm,
  });

  const {
    data: objectTypesData,
    loading: objectTypesloading,
    error: objectTypesError,
  } = useResource(ObjectTypeDefinitions, {
    skip: skipCrm,
  });

  useEffect(() => {
    if (propertyGroupsData) {
      Metrics.counter('fetch-properties-success', {
        object_type_id: objectTypeId,
      }).increment();
    }
  }, [propertyGroupsData, objectTypeId]);

  useEffect(() => {
    if (propertyGroupsError) {
      Metrics.counter('fetch-properties-failure', {
        object_type_id: objectTypeId,
      }).increment();
    }
  }, [propertyGroupsError, objectTypeId]);

  useEffect(() => {
    if (objectTypesData) {
      Metrics.counter('fetch-object-types-success', {
        object_type_id: objectTypeId,
      }).increment();
    }
  }, [objectTypesData, objectTypeId]);

  useEffect(() => {
    if (objectTypesError) {
      Metrics.counter('fetch-object-types-failure', {
        object_type_id: objectTypeId,
      }).increment();
    }
  }, [objectTypesError, objectTypeId]);

  const propertyGroups: PropertyGroupDescriptor[] = useMemo(() => {
    if (!propertyGroupsData || !Array.isArray(propertyGroupsData)) {
      return [];
    }

    return propertyGroupsData.map((propertyGroup) => ({
      ...propertyGroup,
      label: propertyGroup.displayName,
      properties: Array.isArray(propertyGroup.propertyDefinitions)
        ? propertyGroup.propertyDefinitions.map((definition) => ({
            ...definition.property,
            // mostly overlapping string unions
            fieldType: definition.property.fieldType as PropertyFieldTypesType,
            sensitiveDataCategories: definition.property
              .sensitiveDataCategories as SensitiveDataCategoryType[],
            type: definition.property.type as PropertyTypesType,
          }))
        : [],
    }));
  }, [propertyGroupsData]);

  return useMemo(
    () => ({
      error: propertyGroupsError || objectTypesError,
      loading: propertyGroupsloading || objectTypesloading,
      properties: propertyGroups
        .map((propertyGroup) => propertyGroup.properties)
        .flat()
        .filter(
          <T>(property: T | undefined): property is T => property !== undefined
        ),
      propertyGroups,
      typeDefinitions:
        objectTypesData && Array.isArray(objectTypesData)
          ? objectTypesData
          : [],
    }),
    [
      propertyGroupsError,
      objectTypesError,
      propertyGroupsloading,
      objectTypesloading,
      propertyGroups,
      objectTypesData,
    ]
  );
}
